import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, MessageCircle, Share2, Check, Flag } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import CommentSection from '../components/CommentSection';
import FlagModal from '../components/FlagModal';
import Footer from '../components/Footer';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    fetchPost();
    fetchComments();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${postId}`);
      setPost(response.data);
    } catch (error) {
      toast({
        title: 'Failed to load post',
        description: error.response?.status === 404 ? 'Post not found' : 'Please try again',
        variant: 'destructive',
      });
      navigate('/myiniraa');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      
    }
  };

  const handleCommentAdded = (newComment) => {
    setComments([...comments, newComment]);
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/myiniraa/post/${postId}`;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: post.title,
          text: `Check out this post: ${post.title}`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast({
          title: '✓ Link copied!',
          description: 'Post link copied to clipboard',
        });
        
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        
        toast({
          title: 'Could not copy link',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    }
  };


  const getTypeColor = (type) => {
    const colors = {
      Experience: 'bg-blue-100 text-blue-700',
      Question: 'bg-purple-100 text-purple-700',
      Advice: 'bg-green-100 text-green-700',
      Reflection: 'bg-pink-100 text-pink-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F9F9F9] to-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#FF8C00] border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F9F9] to-white py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/myiniraa')}
          className="flex items-center space-x-2 text-gray-600 hover:text-[#FF8C00] transition mb-6"
          data-testid="back-to-community-button"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Community</span>
        </button>

        {/* Post Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8" data-testid="post-detail">
          {/* Post Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <span className={`text-sm px-3 py-1 rounded-full font-medium ${getTypeColor(post.post_type)}`}>
                {post.post_type}
              </span>
              {post.verified && (
                <span className="flex items-center space-x-1 text-sm text-green-600" data-testid="verified-badge">
                  <CheckCircle className="w-4 h-4" />
                  <span>Verified by Iniraa Team</span>
                </span>
              )}
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4" data-testid="post-title">
              {post.title}
            </h1>
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div className="flex items-center space-x-4">
                <span data-testid="post-author">By {post.author_screenname}</span>
                <span className="text-gray-400">•</span>
                <span data-testid="post-date">
                  {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                </span>
                <span className="text-gray-400">•</span>
                <span className="flex items-center space-x-1" data-testid="comment-count">
                  <MessageCircle className="w-4 h-4" />
                  <span>{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</span>
                </span>
              </div>
              
              {/* Share Button */}
              <button
                onClick={handleShare}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition ${
                  copied 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-[#7B3F9E] hover:text-white'
                }`}
                title={copied ? "Copied!" : "Share post"}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span className="text-sm font-medium">Copied!</span>
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4" />
                    <span className="text-sm font-medium">Share</span>
                  </>
                )}
              </button>
              
              {/* Flag Button - show for all authenticated users */}
              {user && (
                <button
                  onClick={() => setShowFlagModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600 transition"
                  title="Report this post"
                >
                  <Flag className="w-4 h-4" />
                  <span className="text-sm font-medium">Report</span>
                </button>
              )}
            </div>
          </div>

          {/* Post Body */}
          <div className="prose prose-lg max-w-none mb-6" data-testid="post-body">
            <p className="text-gray-700 whitespace-pre-wrap">{post.body}</p>
          </div>

          {/* Images */}
          {post.images && post.images.length > 0 && (
            <div className={`mb-6 grid gap-3 ${
              post.images.length === 1 ? 'grid-cols-1' :
              post.images.length === 2 ? 'grid-cols-2' :
              post.images.length === 3 ? 'grid-cols-3' :
              'grid-cols-2'
            }`}>
              {post.images.map((imageUrl, index) => (
                <div
                  key={imageUrl}
                  className="relative overflow-hidden rounded-lg"
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${imageUrl}`}
                    alt={`Post image ${index + 1}`}
                    className="w-full h-auto object-cover cursor-pointer hover:opacity-90 transition"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${imageUrl}`, '_blank')}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-sm px-3 py-1 bg-[#FFF4E6] text-[#FF8C00] rounded-full"
                  data-testid="post-tag"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Verification Notice */}
        {!post.verified && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This post is pending verification by the Iniraa team.
            </p>
          </div>
        )}

        {/* Comments Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <CommentSection
            postId={postId}
            comments={comments}
            onCommentAdded={handleCommentAdded}
          />
        </div>
      </div>

      {/* Flag Modal */}
      {post && (
        <FlagModal
          isOpen={showFlagModal}
          onClose={() => setShowFlagModal(false)}
          contentType="post"
          contentId={postId}
          contentPreview={post.title}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PostDetail;
